/**
 * Catches render-time exceptions so one bad row cannot blank the whole app.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * which previously turned a single unexpected null — a report with no
 * `workspaceName`, an event whose `severity` was absent — into a white page
 * with no explanation and no way back other than a manual reload.
 *
 * Class syntax is required: `componentDidCatch` has no hook equivalent.
 */
import React from "react";

interface Props {
  children: React.ReactNode;
  /** Shown in the message so the user knows how much is affected. */
  label?: string;
  /** Remounts the subtree when this changes, e.g. the current route. */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Kept as console output: there is no frontend telemetry sink in this
    // deployment, and the component stack is what makes these diagnosable.
    console.error("Unhandled render error", error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    // Navigating away from a broken page should not stay broken.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private handleRetry = () => this.setState({ error: null });

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { label } = this.props;

    return (
      <div className="m-6 rounded-xl border border-danger-border bg-danger-subtle p-6">
        <h2 className="text-lg font-semibold text-danger-fg">
          {label ? `Something went wrong in ${label}` : "Something went wrong"}
        </h2>
        <p className="mt-2 text-sm text-content-muted">
          This section failed to render. The rest of the application is still usable.
        </p>
        <pre className="mt-4 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-surface-sunken p-3 text-xs text-content-muted">
          {error.message || String(error)}
        </pre>
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={this.handleRetry}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-fg hover:bg-accent-hover"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-edge-strong px-4 py-2 text-sm font-semibold text-content hover:bg-surface-hover"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
